import { Answer } from '../../../../../types/Survey/Answer/Answer'
import { Element as SurveyElement, Type as SurveyType } from '../../../../../types/Survey/Element'
import {
  Slider,
  SliderGroup,
  Selection,
  SliderDropdownGroup,
  BooleanSelection,
  SliderDropdown,
  NumberInput,
  DynamicNumberDescriptionAmountInput,
  AmountToggleInput,
  DynamicOptionsWithNumberAndSelection,
  OrganizationUnitCooperationSelection,
} from '../../../../../types/Survey/Answer/Element'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import props from '../../../../../redux/props'

type Response = {
  isValid: boolean
  message: string
}

const useCheckIfAnswerIsValid = (answer: Answer, currentElement: SurveyElement): Response => {
  const t = useSelector((s) => s[props.TRANSLATION])
  const [response, setResponse] = useState<Response>({
    isValid: true,
    message: '',
  })

  useEffect(() => {
    const getElement = <T>(answerUUID: string): T => {
      return answer.elements.find((a) => a.uuid === answerUUID) as T
    }

    if (!!answer && !!currentElement) {
      let isValid = true

      if (currentElement.type === SurveyType.DAY_SELECTION) {
        isValid = isFinite(answer.dayNumber)
      } else if (currentElement.type === SurveyType.ORGANIZATION_UNIT_SELECTION) {
        isValid = !!answer.organizationUnitUUID
      } else if (currentElement.type === SurveyType.ORGANIZATION_UNIT_COOPERATION_SELECTION) {
        const answerElement = getElement<OrganizationUnitCooperationSelection>(currentElement.uuid)
        const selectedUnits = answerElement.organizationUnitPrioSelectionList
        if (selectedUnits.length === 0) {
          isValid = true
        }
        isValid = selectedUnits.reduce((acc, s) => {
          if (!acc) return false
          return !!s.organizationUnitUUID && s.priority > 0
        }, true)
      } else if (currentElement.type === SurveyType.STATIC_TEXT) {
        isValid = true
      } else if (currentElement.type === SurveyType.SLIDER_GROUP) {
        const answerElement = getElement<SliderGroup>(currentElement.uuid)
        isValid = answerElement.sliders.reduce((acc, s) => acc + s.value, 0) === 100
      } else if (currentElement.type === SurveyType.SLIDER) {
        const answerElement = getElement<Slider>(currentElement.uuid)
        isValid = isFinite(answerElement.value)
      } else if (currentElement.type === SurveyType.SELECTION) {
        const answerElement = getElement<Selection>(currentElement.uuid)
        isValid = answerElement.selectedOptions.length > 0
      } else if (currentElement.type === SurveyType.DYNAMIC_TEXT) {
        isValid = true
      } else if (currentElement.type === SurveyType.SLIDER_DROPDOWN_GROUP) {
        const answerElement = getElement<SliderDropdownGroup>(currentElement.uuid)
        const sum = answerElement.sliderDropdowns.reduce((acc, s) => acc + s.value, 0) === 100
        const optionsSelected = answerElement.sliderDropdowns.reduce((acc, s) => {
          if (!acc) return false
          if (s.value === 0) return true
          return !!s.optionUUID
        }, true)
        isValid = sum && optionsSelected
      } else if (currentElement.type === SurveyType.SLIDER_DROPDOWN) {
        const answerElement = getElement<SliderDropdown>(currentElement.uuid)
        isValid = isFinite(answerElement.value) && !!answerElement.optionUUID
      } else if (currentElement.type === SurveyType.BOOLEAN_SELECTION) {
        const answerElement = getElement<BooleanSelection>(currentElement.uuid)
        isValid = !!answerElement.selectionValue
      } else if (currentElement.type === SurveyType.NUMBER_INPUT) {
        const answerElement = getElement<NumberInput>(currentElement.uuid)
        isValid = isFinite(answerElement.number)
      } else if (currentElement.type === SurveyType.DYNAMIC_NUMBER_DESCRIPTION_AMOUNT_INPUT) {
        const answerElement = getElement<DynamicNumberDescriptionAmountInput>(currentElement.uuid)
        isValid = answerElement.numberAnswers.reduce((acc, s) => {
          if (!acc) return false
          if (s.description.length > 0) {
            return s.amount > 0 && s.number > 0
          }
          return true
        }, true)
      } else if (currentElement.type === SurveyType.AMOUNT_TOGGLE_INPUT) {
        const answerElement = getElement<AmountToggleInput>(currentElement.uuid)
        if (!answerElement.isEnabled) isValid = true
        isValid = isFinite(answerElement.amount)
      } else if (currentElement.type === SurveyType.ORGANIZATION_UNIT_NUMBER_INPUT) {
        isValid = true
      } else if (currentElement.type === SurveyType.DYNAMIC_OPTIONS_WITH_NUMBER_AND_SELECTION) {
        const answerElement = getElement<DynamicOptionsWithNumberAndSelection>(currentElement.uuid)
        isValid = answerElement.dynamicOptionsWithNumberAndSelection.reduce((acc, s) => {
          if (!acc) return false
          if (!s.isActive) return true
          return (s.number > 0 || s.isNormSelected) && !!s.name && s.name.length > 0
        }, true)
      }

      setResponse({
        isValid,
        message: t.survey.answer.validation.messages[currentElement.type],
      })
    }
  }, [answer, currentElement, t])

  return response
}

export default useCheckIfAnswerIsValid
